import { Disposable } from '../DisposableContainer/DisposableContainer';

export class RateLimit {
  private constructor() {}

  public static rateLimitFunction<T extends RateLimitedFunctionCallback>(
    callback: T,
    rateInterval: number
  ): RateLimitedFunction<T> {
    let timeout: number | null = null;

    /**
     * a callback call is currently in progress, this is only really relevant if the callback is promise
     */
    let callbackIsBeingCalled = false;
    /**
     * the callback has been called while the call is already in progress
     * this is important so we won't have 2 async callbacks running at the same time
     * if this is true, the callback will be called again after the previous call finishes
     */
    let callbackHasBeenCalledAgain = false;
    /**
     * gets called once after all callback calls have been digested
     * e.g. if the callback is async and callbackHasBeenCalledAgain = true, then this will be called after the second call of callback
     */
    let afterCallbackFinished: Array<() => void> = [];

    const callFinished = (): void => {
      callbackIsBeingCalled = false;

      if (callbackHasBeenCalledAgain) {
        callbackHasBeenCalledAgain = false;
        callCallback();
      } else {
        afterCallbackFinished.forEach((cb) => cb());
        afterCallbackFinished = [];
      }
    };

    const callCallback = (): void => {
      if (callbackIsBeingCalled) {
        callbackHasBeenCalledAgain = true;
        return;
      }

      callbackIsBeingCalled = true;

      try {
        const result = callback();
        if (result instanceof Promise) {
          void result.finally(callFinished);
        } else {
          callFinished();
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {
        callFinished();
      }
    };

    const f: RateLimitedFunction<T> = () => {
      if (!timeout) {
        timeout = window.setTimeout(() => {
          timeout = null;
          callCallback();
        }, rateInterval);
      }
    };

    f.cancel = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;

        // in case the callback is running, prevent it from running again
        callbackHasBeenCalledAgain = false;
      }
    };

    f.isPending = () => {
      return !!timeout || callbackIsBeingCalled;
    };

    f.callImmediatelyIfPending = (() => {
      if (timeout || callbackIsBeingCalled) {
        f.cancel();

        callCallback();

        if (callbackIsBeingCalled) {
          // only return a promise for async callbacks
          // if the callback is sync, then the callbackIsBeingCalled would already be false again
          return new Promise<void>((res) => {
            afterCallbackFinished.push(res);
          });
        }
      }

      return undefined;
    }) as RateLimitedFunction<T>['callImmediatelyIfPending']; // since typescript doesn't understand our custom logic, we have to manually cast it

    f.toCancelDisposable = () => {
      return {
        dispose: () => f.cancel()
      };
    };

    return f;
  }
}

export type RateLimitedFunction<T extends RateLimitedFunctionCallback> = {
  (): void;

  /* cancels the pending func call */
  cancel: () => void;
  isPending: () => boolean;
  callImmediatelyIfPending: CallImmediatelyIfPendingReturnType<T>;

  toCancelDisposable: () => Disposable;
};

export type RateLimitedFunctionCallback = (() => void) | (() => Promise<void>);

type CallImmediatelyIfPendingReturnType<T extends RateLimitedFunctionCallback> =
  () => ReturnType<T> extends Promise<any>
    ? Promise<void> | undefined
    : undefined;
